import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Set or change a password — the account menu's two small dialogs (`studio/07`).
 *
 * One component, two shapes: with `askCurrent` it asks for the current password first
 * (Firebase wants a recent sign-in before a password changes, and the current password is
 * how a password account proves one); without it, it asks only for the new one — a link or
 * Google account setting its first password.
 *
 * Like the sign-in page it holds **no copy** and talks to nothing: it is handed one submit
 * action and one function that turns whatever it throws into a line. The eight-character
 * minimum is checked here, before the network; a stricter console policy wins and its error
 * comes back through `describe`.
 */
import { useState } from 'react';
import { Button } from './Button.js';
import { Dialog } from './Dialog.js';
import { Field } from './Field.js';
import { PASSWORD_MIN_LENGTH } from './SignIn.js';
export function PasswordDialog({ open, onClose, strings: s, askCurrent, onSubmit, describe, }) {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [currentError, setCurrentError] = useState(undefined);
    const [nextError, setNextError] = useState(undefined);
    const [problem, setProblem] = useState(null);
    const [working, setWorking] = useState(false);
    function reset() {
        setCurrent('');
        setNext('');
        setCurrentError(undefined);
        setNextError(undefined);
        setProblem(null);
        setWorking(false);
    }
    function close() {
        reset();
        onClose();
    }
    async function submit() {
        let valid = true;
        if (askCurrent && current === '') {
            setCurrentError(s.required);
            valid = false;
        }
        else {
            setCurrentError(undefined);
        }
        if (next.length < PASSWORD_MIN_LENGTH) {
            setNextError(s.tooShort);
            valid = false;
        }
        else {
            setNextError(undefined);
        }
        if (!valid)
            return;
        setProblem(null);
        setWorking(true);
        try {
            await onSubmit(askCurrent ? current : undefined, next);
            close();
        }
        catch (error) {
            setProblem(await describe(error));
            setWorking(false);
        }
    }
    return (_jsxs(Dialog, { open: open, onClose: close, title: s.title, actions: _jsxs(_Fragment, { children: [_jsx(Button, { tone: "quiet", isDisabled: working, onClick: close, children: s.cancel }), _jsx(Button, { tone: "primary", working: working, isDisabled: working, onClick: () => void submit(), children: working ? s.working : s.confirm })] }), children: [s.body !== undefined && _jsx("p", { className: "note", children: s.body }), _jsxs("div", { className: "stack", children: [askCurrent && (_jsx(Field, { label: s.current, type: "password", autoComplete: "current-password", value: current, ...(currentError !== undefined ? { error: currentError } : {}), onChange: setCurrent })), _jsx(Field, { label: s.next, type: "password", autoComplete: "new-password", help: s.nextHelp, value: next, ...(nextError !== undefined ? { error: nextError } : {}), onChange: setNext, onKeyDown: (event) => {
                            if (event.key === 'Enter')
                                void submit();
                        } }), problem !== null && (_jsx("p", { className: "signin-problem", role: "alert", children: problem }))] })] }));
}
//# sourceMappingURL=PasswordDialog.js.map